// store/api/contactListApi.ts
//
// Contact lists — plain saved-contact lists (the "contact" type) backed by the
// Infynd-CL-List-Management service. The browser talks to our own Express proxy
// at /api/contact-lists/* (mounted in packages/server/routes.ts), which resolves
// the user from the session and forwards to the list-management service. The
// upstream bearer token stays server side.
//
// Endpoints (Express proxy -> list-management):
//   GET    /api/contact-lists                       fetchAllListsWithCsv
//   POST   /api/contact-lists                       addToList (create)
//   PUT    /api/contact-lists/:listId               addToList (append)
//   GET    /api/contact-lists/:listId/recipients    getListRecipients
//   DELETE /api/contact-lists/:listId/recipients    deleteListRecipients
//   PATCH  /api/contact-lists/:listId               renameContactList
//   DELETE /api/contact-lists/:listId               deleteContactList
//   POST   /api/contact-lists/delete-lists          deleteContactLists (bulk)
//   POST   /api/contact-lists/:listId/copy          copyList

import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import type { AllListsResponse, ListContact } from '@/lib/types/list.types';

export interface AddToListArgs {
  type: 'contact';
  listId?: string;
  json: {
    userId: string;
    name: string;
    source?: 'Infynd' | 'csv' | 'system';
    recipients: Array<string | Record<string, unknown>>;
    action?: 'add';
    pageFlag?: string;
    newCreditUserFlag?: boolean;
  };
}

export interface AddToListResponse {
  success: boolean;
  id?: string;
  rowcount?: number;
  duplicates?: number;
  name?: string;
  listLength?: number;
  res?: string;
  message?: string;
  error?: string;
}

export interface ListRecipientsResponse {
  unlockContacts: ListContact[];
  total: number;
}

export const contactListApi = createApi({
  reducerPath: 'contactListApi',
  baseQuery: axiosBaseQuery('/api/contact-lists'),
  tagTypes: ['ContactList', 'ListRecipients'],
  endpoints: (build) => ({
    fetchAllListsWithCsv: build.query<
      AllListsResponse,
      { userId: string; limit?: number; start?: number; search?: string }
    >({
      query: (payload) => ({
        url: '',
        method: 'GET',
        params: {
          userId: payload.userId,
          limit: payload.limit ?? 10,
          start: payload.start ?? 0,
          search: payload.search ?? '',
        },
      }),
      providesTags: [{ type: 'ContactList', id: 'LIST' }],
    }),

    addToList: build.mutation<AddToListResponse, AddToListArgs>({
      query: ({ listId, json }) => {
        if (listId) {
          return {
            url: `/${listId}`,
            method: 'PUT',
            body: { recipients: json.recipients },
          };
        }
        return {
          url: '',
          method: 'POST',
          body: { name: json.name, recipients: json.recipients },
        };
      },
      invalidatesTags: [
        { type: 'ContactList', id: 'LIST' },
        { type: 'ListRecipients', id: 'LIST' },
      ],
    }),

    // A query, not a mutation: it is a plain GET, and addToList/deleteListRecipients
    // below already invalidate ListRecipients. Without a provider for that tag those
    // invalidations had nothing to refetch, so callers had to reload by hand.
    getListRecipients: build.query<
      ListRecipientsResponse,
      { listId: string; limit?: number; start?: number; searchword?: string }
    >({
      query: (payload) => ({
        url: `/${payload.listId}/recipients`,
        method: 'GET',
        params: {
          limit: payload.limit ?? 10,
          start: payload.start ?? 0,
          searchword: payload.searchword ?? '',
        },
      }),
      providesTags: [{ type: 'ListRecipients', id: 'LIST' }],
    }),

    deleteListRecipients: build.mutation<
      { success: boolean; message?: string },
      { listId: string; emailIds: string[] }
    >({
      query: (payload) => ({
        url: `/${payload.listId}/recipients`,
        method: 'DELETE',
        body: { emailIds: payload.emailIds },
      }),
      invalidatesTags: [
        { type: 'ListRecipients', id: 'LIST' },
        { type: 'ContactList', id: 'LIST' },
      ],
    }),

    renameContactList: build.mutation<{ success: boolean }, { listId: string; listName: string }>({
      query: (payload) => ({
        url: `/${payload.listId}`,
        method: 'PATCH',
        body: { name: payload.listName },
      }),
      invalidatesTags: [{ type: 'ContactList', id: 'LIST' }],
    }),

    deleteContactList: build.mutation<{ success: boolean }, { listId: string }>({
      query: (payload) => ({
        url: `/${payload.listId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ContactList', id: 'LIST' }],
    }),

    // One round trip instead of N - mirrors listApi.ts's deleteLists
    // (used by the suppression tables' own bulk delete).
    deleteContactLists: build.mutation<{ success: boolean; deleted: number }, { listIds: string[] }>({
      query: (payload) => ({
        url: `/delete-lists`,
        method: 'POST',
        body: { listIds: payload.listIds },
      }),
      invalidatesTags: [{ type: 'ContactList', id: 'LIST' }],
    }),

    copyList: build.mutation<
      { success: boolean; id?: string; name?: string },
      { listId: string; listName: string }
    >({
      query: (payload) => ({
        url: `/${payload.listId}/copy`,
        method: 'POST',
        body: { listName: payload.listName },
      }),
      invalidatesTags: [{ type: 'ContactList', id: 'LIST' }],
    }),
  }),
});

export const {
  useFetchAllListsWithCsvQuery,
  useLazyFetchAllListsWithCsvQuery,
  useAddToListMutation,
  useGetListRecipientsQuery,
  useLazyGetListRecipientsQuery,
  useDeleteListRecipientsMutation,
  useRenameContactListMutation,
  useDeleteContactListMutation,
  useDeleteContactListsMutation,
  useCopyListMutation,
} = contactListApi;
